import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { CheckCircle2, ChevronDown, ChevronUp, GripVertical } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import type { AnswerInputProps } from '../QuestionRenderer'

export function RankingInput({
  question,
  value,
  onChange,
  onSubmit,
  showInlineSubmit = true,
}: AnswerInputProps<string[]>) {
  const choiceValues = useMemo(
    () => question.choices.map((choice) => choice.value),
    [question.choices],
  )
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const order = useMemo(() => {
    const current = (value ?? []).filter((choiceValue) => choiceValues.includes(choiceValue))
    const missing = choiceValues.filter((choiceValue) => !current.includes(choiceValue))
    return current.length > 0 ? [...current, ...missing] : choiceValues
  }, [choiceValues, value])

  useEffect(() => {
    if (order.length === 0) return
    if (sameOrder(value ?? [], order)) return
    onChange(order)
  }, [onChange, order, value])

  function onDragEnd(event: DragEndEvent) {
    if (!event.over || event.active.id === event.over.id) return
    const oldIndex = order.indexOf(String(event.active.id))
    const newIndex = order.indexOf(String(event.over.id))
    if (oldIndex < 0 || newIndex < 0) return
    onChange(arrayMove(order, oldIndex, newIndex))
  }

  function move(index: number, direction: -1 | 1) {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= order.length) return
    onChange(arrayMove(order, index, nextIndex))
  }

  if (question.choices.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-500">
        尚未設定排序項目，請在右側問題設定新增項目。
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        className="rounded-md border px-3 py-2 text-sm"
        style={{
          background: 'color-mix(in srgb, var(--form-accent,#6366F1) 10%, var(--form-bg,#F8FAFC))',
          borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
          color: 'var(--form-answer,#334155)',
          fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
        }}
      >
        拖拉項目或使用上下移按鈕調整排序。
      </div>
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {order.map((choiceValue, index) => {
              const choice = question.choices.find((item) => item.value === choiceValue)
              return (
                <RankingRow
                  key={choiceValue}
                  id={choiceValue}
                  index={index}
                  label={choice?.label ?? choiceValue}
                  canMoveUp={index > 0}
                  canMoveDown={index < order.length - 1}
                  onMoveUp={() => move(index, -1)}
                  onMoveDown={() => move(index, 1)}
                />
              )
            })}
          </div>
        </SortableContext>
      </DndContext>
      {showInlineSubmit ? (
        <button
          type="button"
          onClick={onSubmit}
          className="inline-flex min-h-11 w-fit items-center gap-2 rounded-md px-4 font-medium"
          style={{
            background: 'var(--form-button,#6366F1)',
            borderRadius: 'var(--form-radius,8px)',
            color: 'var(--form-button-text,#FFFFFF)',
            fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
          }}
        >
          <CheckCircle2 className="size-4" />
          {question.settings.buttonText ?? '確認排序'}
        </button>
      ) : null}
    </div>
  )
}

function RankingRow({
  id,
  index,
  label,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
}: {
  id: string
  index: number
  label: string
  canMoveUp: boolean
  canMoveDown: boolean
  onMoveUp: () => void
  onMoveDown: () => void
}) {
  const sortable = useSortable({ id })
  return (
    <div
      ref={sortable.setNodeRef}
      className="flex min-h-12 items-center gap-3 rounded-md border p-3 shadow-sm"
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 86%, white)',
        borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
        borderRadius: 'var(--form-radius,8px)',
        color: 'var(--form-answer,#334155)',
        fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
      }}
    >
      <button
        ref={sortable.setActivatorNodeRef}
        type="button"
        {...sortable.attributes}
        {...sortable.listeners}
        className="grid size-9 touch-none place-items-center rounded-md border"
        style={{
          borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 20%, transparent)',
          color: 'var(--form-answer,#64748B)',
        }}
        aria-label={`拖移 ${label} 排序`}
      >
        <GripVertical className="size-4" />
      </button>
      <span
        className="grid size-7 place-items-center rounded-full text-sm font-semibold"
        style={{
          background: 'color-mix(in srgb, var(--form-accent,#6366F1) 16%, transparent)',
          color: 'var(--form-accent,#6366F1)',
        }}
      >
        {index + 1}
      </span>
      <span className="min-w-0 flex-1 font-medium">{label}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!canMoveUp}
          className="grid size-9 place-items-center rounded-md border disabled:cursor-not-allowed disabled:opacity-35"
          style={{
            borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 20%, transparent)',
            color: 'var(--form-answer,#64748B)',
          }}
          aria-label={`上移 ${label}`}
        >
          <ChevronUp className="size-4" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!canMoveDown}
          className="grid size-9 place-items-center rounded-md border disabled:cursor-not-allowed disabled:opacity-35"
          style={{
            borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 20%, transparent)',
            color: 'var(--form-answer,#64748B)',
          }}
          aria-label={`下移 ${label}`}
        >
          <ChevronDown className="size-4" />
        </button>
      </div>
    </div>
  )
}

function sameOrder(left: string[], right: string[]) {
  if (left.length !== right.length) return false
  return left.every((item, index) => item === right[index])
}
